import { useRef, useState } from "react";
import { useFormik } from "formik";
import { Button } from "@design-system/Button/Button";
import { Picker } from "@design-system/Picker/Picker.lib";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { HideIcon, PlusIcon } from "@icons";
import { buildFlatItems, plainFlatItemOptions } from "./PickerPlayground.lib";
import { buildProductItems } from "./PickerPlaygroundProducts";
import { buildTreeItems } from "./PickerPlaygroundTree";
import styles from "./PickerPlaygroundUsage.module.scss";

const items = buildFlatItems(plainFlatItemOptions);

const treeItems = buildTreeItems({
    hasDescriptions: false,
    hasRightText: false,
});

const productItems = buildProductItems((_product, query) => (
    <Picker.Item leading={<PlusIcon />} onSelect={() => undefined}>
        {query.trim() === "" ? "Add price" : `Add "${query.trim()}"`}
    </Picker.Item>
));

// #region select
export const SelectExample = () => {
    const [value, setValue] = useState<string | null>(null);
    return (
        <Picker.Select
            items={items}
            value={value}
            onValueChange={setValue}
            placeholder="Choose an account"
        />
    );
};
// #endregion

// #region searchable
export const SearchableExample = () => {
    const [value, setValue] = useState<string | null>(null);
    return (
        <Picker.Select
            items={items}
            searchable
            clearable
            placeholder="Choose an account"
            searchPlaceholder="Search accounts"
            value={value}
            onValueChange={setValue}
        />
    );
};
// #endregion

// #region multiple
export const MultipleExample = () => {
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    return (
        <Picker.Select
            items={items}
            multiple
            value={selectedValues}
            onValueChange={setSelectedValues}
            placeholder="Choose accounts"
            renderValue={(values) =>
                values.length === 0
                    ? "Choose accounts"
                    : `${values.length} selected`
            }
        />
    );
};
// #endregion

// #region customValue
export const CustomValueExample = () => {
    const [value, setValue] = useState("");
    return (
        <Picker.Select
            trigger="input"
            allowCustomValue
            items={items}
            value={value}
            onValueChange={setValue}
            placeholder="Describe the expense"
            renderFooter={(query) =>
                query.trim() === "" ? null : (
                    <Picker.Item
                        leading={<PlusIcon />}
                        onSelect={() => setValue(query)}
                    >
                        {`Add "${query.trim()}"`}
                    </Picker.Item>
                )
            }
        />
    );
};
// #endregion

// #region inputTrigger
export const InputTriggerExample = () => {
    const [value, setValue] = useState<string | null>(null);
    return (
        <Picker.Select
            trigger="input"
            size="lg"
            items={items}
            value={value}
            onValueChange={setValue}
            placeholder="Choose an account"
        />
    );
};
// #endregion

// #region cellTrigger
export const CellTriggerExample = () => {
    const [value, setValue] = useState<string | null>(null);
    return (
        <div>
            <Picker.Select
                trigger="cell"
                items={items}
                value={value}
                onValueChange={setValue}
                placeholder="Choose"
                buttonProps={{ tooltip: "Choose a category" }}
            />
        </div>
    );
};
// #endregion

// #region attached
export const AttachedExample = () => {
    const composerRef = useRef<HTMLDivElement>(null);
    const [value, setValue] = useState<string | null>(null);
    return (
        <div className={styles.attachedFrame}>
            <Picker.Select
                trigger="attached"
                attachedTo={composerRef}
                title="Chat groups"
                icon={<HideIcon />}
                items={items}
                value={value}
                onValueChange={setValue}
                placeholder="Choose a group"
            />
            <div ref={composerRef} className={styles.attachedBox}>
                Ask anything
            </div>
        </div>
    );
};
// #endregion

// #region tree
export const TreeExample = () => {
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    return (
        <Picker.Select
            items={treeItems}
            nested="tree"
            multiple
            parentSelection="cascade"
            searchable
            value={selectedValues}
            onValueChange={setSelectedValues}
            placeholder="Choose accounts"
        />
    );
};
// #endregion

// #region flyout
export const FlyoutExample = () => {
    const [value, setValue] = useState<string | null>(null);
    return (
        <Picker.Select
            items={productItems}
            nested="flyout"
            searchable
            searchPlaceholder="Search products"
            flyoutDefaults={{
                searchable: true,
                searchPlaceholder: "Search this level",
            }}
            value={value}
            onValueChange={setValue}
            placeholder="Choose a product"
        />
    );
};
// #endregion

// #region buttonElement
export const ButtonElementExample = () => {
    const [value, setValue] = useState<string | null>(null);
    return (
        <Picker.Select
            items={items}
            trigger={
                <Picker.Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<PlusIcon />}
                />
            }
            chevron="hover"
            value={value}
            onValueChange={setValue}
            placeholder="Choose an account"
        />
    );
};
// #endregion

// #region composed
export const ComposedExample = () => (
    <Picker.Select searchable>
        <Tooltip content="Choose an account">
            <Picker.Trigger
                render={<Button variant="primary">Choose account</Button>}
            />
        </Tooltip>
        <Picker.Popup>
            <Picker.Search placeholder="Search accounts" />
            <Picker.List>
                <Picker.Section>
                    <Picker.SectionTitle>Accounts</Picker.SectionTitle>
                    <Picker.Item value="operating">Operating</Picker.Item>
                </Picker.Section>
            </Picker.List>
            <Picker.Footer>
                <Picker.Item onSelect={() => undefined}>
                    Add account
                </Picker.Item>
            </Picker.Footer>
        </Picker.Popup>
    </Picker.Select>
);
// #endregion

// #region formik
export const FormikExample = () => {
    const triggerRef = useRef<HTMLButtonElement>(null);
    const formik = useFormik<{ account: string | null }>({
        initialValues: { account: null },
        initialTouched: { account: true },
        validateOnMount: true,
        validate: ({ account }) =>
            account === null ? { account: "Choose an account" } : {},
        onSubmit: () => undefined,
    });
    const hasError = Boolean(formik.touched.account && formik.errors.account);
    return (
        <Picker.Select
            items={items}
            name="account"
            value={formik.values.account}
            onValueChange={(account) =>
                formik.setFieldValue("account", account)
            }
            onBlur={() => formik.setFieldTouched("account")}
            ref={triggerRef}
            aria-invalid={hasError}
            placeholder="Choose an account"
        />
    );
};
// #endregion
